import React, {useCallback} from 'react';
import {
  ExpressionEditor,
  extendExpressionEditorActionAtPath,
  updateExpressionAtAction,
} from '@packages/systems/ExpressionEditor-utils';
import {typeName, qualify} from '@packages/systems/wfdl-Qualified';
import {
  type ContextualExpression,
  ContextualExpressions,
  stripAnn,
} from '@packages/utilities/contextual-expression';
import {once} from '@packages/utilities/memo';
import {
  TRecord,
  ELiteral,
  Expression,
  ERecord,
} from '@packages/systems/wfdl-ast';
import {
  emptyCheckerState,
  inferType,
  withSystem,
  setType,
  Plugins,
  TypeImplementation,
  Type,
} from '@packages/systems/wfdl';
import {constantFalse} from '@packages/utilities/fp/utils';
import {
  getBoolean,
  get as getFromJson,
  getString,
  matchJson,
} from '@packages/utilities/json';
import {VStack, Text, Divider} from '@packages/systems/spring';
import {selectSelectedElement} from '@packages/utilities/platform-selectors';
import {RiveProvider} from './RiveContext';
import {
  ALIGNMENT_PATH,
  AUTO_PLAY_PATH,
  STATE_MACHINE_PATH,
  FIT_PATH,
  AUTOMATICALLY_HANDLE_EVENTS_PATH,
  IS_TOUCH_SCROLL_ENABLED_PATH,
  PATH_TYPE,
  ARTBOARD_TYPE,
  STATE_MACHINE_TYPE,
  AUTO_PLAY_TYPE,
  IS_TOUCH_SCROLL_ENABLED_TYPE,
  FIT_TYPE,
  ALIGNMENT_TYPE,
  AUTOMATICALLY_HANDLE_EVENTS_TYPE,
  ARTBOARD_NAME,
  STATE_MACHINE_NAME,
  AUTO_PLAY_NAME,
  IS_TOUCH_SCROLL_ENABLED_NAME,
  AUTOMATICALLY_HANDLE_EVENTS_NAME,
  ALIGNMENT_NAME,
  FIT_NAME,
  PATH_NAME,
} from './constants';
import {useDesignerSelector} from '@packages/systems/designer/flux';

const createContextualExpressionDefault = (expr: Expression, type: Type) => {
  const [, contextualExpr] = inferType(expr)(emptyCheckerState);
  return setType(contextualExpr, withSystem({isBindable: false}, type));
};

const riveAliasType = TRecord({
  path: withSystem({isLocalizable: true}, PATH_TYPE),
  // artboard change rewrites the full Rive record (LOC-2412) — leave unmarked
  artboard: ARTBOARD_TYPE,
  stateMachine: withSystem({isLocalizable: true}, STATE_MACHINE_TYPE),
  autoPlay: withSystem({isLocalizable: true}, AUTO_PLAY_TYPE),
  isTouchScrollEnabled: withSystem(
    {isLocalizable: true},
    IS_TOUCH_SCROLL_ENABLED_TYPE
  ),
  fit: withSystem({isLocalizable: true}, FIT_TYPE),
  alignment: withSystem({isLocalizable: true}, ALIGNMENT_TYPE),
  automaticallyHandleEvents: withSystem(
    {isLocalizable: true},
    AUTOMATICALLY_HANDLE_EVENTS_TYPE
  ),
});

const artboardDefault = once(() =>
  createContextualExpressionDefault(
    ELiteral({
      name: ARTBOARD_NAME,
      value: null,
    }),
    ARTBOARD_TYPE
  )
);

const stateMachineDefault = once(() =>
  createContextualExpressionDefault(
    ELiteral({
      name: STATE_MACHINE_NAME,
      value: null,
    }),
    STATE_MACHINE_TYPE
  )
);

const autoPlayDefault = once(() =>
  createContextualExpressionDefault(
    ELiteral({
      name: AUTO_PLAY_NAME,
      value: true,
    }),
    AUTO_PLAY_TYPE
  )
);

const isTouchScrollEnabledDefault = once(() =>
  createContextualExpressionDefault(
    ELiteral({
      name: IS_TOUCH_SCROLL_ENABLED_NAME,
      value: false,
    }),
    IS_TOUCH_SCROLL_ENABLED_TYPE
  )
);
const automaticallyHandleEventsDefault = once(() =>
  createContextualExpressionDefault(
    ELiteral({
      name: AUTOMATICALLY_HANDLE_EVENTS_NAME,
      value: false,
    }),
    AUTOMATICALLY_HANDLE_EVENTS_TYPE
  )
);

const fitDefault = once(() =>
  createContextualExpressionDefault(
    ELiteral({
      name: FIT_NAME,
      value: 'contain',
    }),
    FIT_TYPE
  )
);
const alignmentDefault = once(() =>
  createContextualExpressionDefault(
    ELiteral({
      name: ALIGNMENT_NAME,
      value: 'center',
    }),
    ALIGNMENT_TYPE
  )
);

const getFieldFromExpr = (expr: ContextualExpression, fieldName: string) =>
  ContextualExpressions.getIn(expr, [{in: 'Record', at: fieldName}]);

const getBooleanFromLiteral = (
  _expr: ContextualExpression,
  defaultValue: boolean
) => {
  const literal = ContextualExpressions.getLiteral(stripAnn(_expr));
  const value = literal ? getBoolean(literal.value) : undefined;
  if (value === undefined) return defaultValue;
  return value;
};
const getStringFromLiteral = (
  _expr: ContextualExpression,
  defaultValue: string | null
) => {
  const literal = ContextualExpressions.getLiteral(stripAnn(_expr));
  const value = literal ? getString(literal.value) : undefined;
  if (value === undefined) return defaultValue;
  return value;
};
const getObjectFromLiteral = (
  _expr: ContextualExpression,
  defaultValue: null
) => {
  const literal = ContextualExpressions.getLiteral(stripAnn(_expr));
  if (!literal) return defaultValue;
  const srcJson = getFromJson(literal.value, ['src']);
  const origFileNameJson = getFromJson(literal.value, ['origFileName']);
  const idJson = getFromJson(literal.value, ['id']);
  const src = srcJson ? getString(srcJson) : null;
  const origFileName = origFileNameJson ? getString(origFileNameJson) : null;
  const id = idJson ? getString(idJson) : null;
  if (!src || !origFileName) return defaultValue;
  return {
    src,
    origFileName,
    ...(id ? {id} : null),
  };
};

const matchSrcJson = matchJson({
  Null: constantFalse,
  Boolean: constantFalse,
  Number: constantFalse,
  String: constantFalse,
  Array: constantFalse,
  Object: (fields) => Boolean(fields),
});

const RiveRender = ({
  expr,
  dispatch,
}: {
  expr: ContextualExpression;
  dispatch: TypeImplementation.ExpressionEditorDispatch;
}) => {
  const [
    path,
    artboard,
    stateMachine,
    autoPlay,
    isTouchScrollEnabled,
    fit,
    alignment,
    automaticallyHandleEvents,
  ] = [
    getFieldFromExpr(expr, 'path'),
    getFieldFromExpr(expr, 'artboard'),
    getFieldFromExpr(expr, 'stateMachine'),
    getFieldFromExpr(expr, 'autoPlay'),
    getFieldFromExpr(expr, 'isTouchScrollEnabled'),
    getFieldFromExpr(expr, 'fit'),
    getFieldFromExpr(expr, 'alignment'),
    getFieldFromExpr(expr, 'automaticallyHandleEvents'),
  ];

  const eeDispatch = useCallback(
    (property: string) => {
      return extendExpressionEditorActionAtPath(dispatch)([
        {in: 'Record', at: property},
      ]);
    },
    [dispatch]
  );

  const pathValue = path ? getObjectFromLiteral(path, null) : null;
  const artboardValue = artboard ? getStringFromLiteral(artboard, null) : null;
  const stateMachineValue = stateMachine
    ? getStringFromLiteral(stateMachine, null)
    : null;
  const fitValue = fit ? getStringFromLiteral(fit, null) : null;
  const alignmentValue = alignment
    ? getStringFromLiteral(alignment, null)
    : null;
  const autoPlayValue = autoPlay
    ? getBooleanFromLiteral(autoPlay, false)
    : false;
  const isTouchScrollEnabledValue = isTouchScrollEnabled
    ? getBooleanFromLiteral(isTouchScrollEnabled, false)
    : false;
  const automaticallyHandleEventsValue = automaticallyHandleEvents
    ? getBooleanFromLiteral(automaticallyHandleEvents, false)
    : false;

  const hasSrc = matchSrcJson(pathValue);

  const onStateMachineChange = React.useCallback(
    (value: string) => {
      dispatch(
        updateExpressionAtAction(
          ELiteral({
            name: qualify(
              Plugins.name('Animation'),
              typeName('RiveStateMachine')
            ),
            value,
          }),
          STATE_MACHINE_PATH
        )
      );
    },
    [dispatch]
  );

  const onArtboardChange = React.useCallback(
    (value: string) => {
      dispatch(
        updateExpressionAtAction(
          ERecord({
            path: ELiteral({
              name: PATH_NAME,
              value: pathValue,
            }),
            artboard: ELiteral({
              name: ARTBOARD_NAME,
              value,
            }),
            stateMachine: ELiteral({
              name: STATE_MACHINE_NAME,
              value: null,
            }),
            autoPlay: ELiteral({
              name: AUTO_PLAY_NAME,
              value: autoPlayValue,
            }),
            isTouchScrollEnabled: ELiteral({
              name: IS_TOUCH_SCROLL_ENABLED_NAME,
              value: isTouchScrollEnabledValue,
            }),
            fit: ELiteral({
              name: FIT_NAME,
              value: fitValue,
            }),
            alignment: ELiteral({
              name: ALIGNMENT_NAME,
              value: alignmentValue,
            }),
            automaticallyHandleEvents: ELiteral({
              name: AUTOMATICALLY_HANDLE_EVENTS_NAME,
              value: automaticallyHandleEventsValue,
            }),
          }),
          []
        )
      );
    },
    [
      dispatch,
      pathValue,
      autoPlayValue,
      isTouchScrollEnabledValue,
      fitValue,
      alignmentValue,
      automaticallyHandleEventsValue,
    ]
  );
  const onAutoPlayChange = React.useCallback(
    (value: boolean) => {
      dispatch(
        updateExpressionAtAction(
          ELiteral({
            name: AUTO_PLAY_NAME,
            value,
          }),
          AUTO_PLAY_PATH
        )
      );
    },
    [dispatch]
  );
  const onIsTouchScrollEnabledChange = React.useCallback(
    (value: boolean) => {
      dispatch(
        updateExpressionAtAction(
          ELiteral({
            name: IS_TOUCH_SCROLL_ENABLED_NAME,
            value,
          }),
          IS_TOUCH_SCROLL_ENABLED_PATH
        )
      );
    },
    [dispatch]
  );
  const onAutomaticallyHandleEventsChange = React.useCallback(
    (value: boolean) => {
      dispatch(
        updateExpressionAtAction(
          ELiteral({
            name: AUTOMATICALLY_HANDLE_EVENTS_NAME,
            value,
          }),
          AUTOMATICALLY_HANDLE_EVENTS_PATH
        )
      );
    },
    [dispatch]
  );
  const onFitChange = React.useCallback(
    (value: string) => {
      dispatch(
        updateExpressionAtAction(
          ELiteral({
            name: FIT_NAME,
            value,
          }),
          FIT_PATH
        )
      );
    },
    [dispatch]
  );
  const onAlignmentChange = React.useCallback(
    (value: string) => {
      dispatch(
        updateExpressionAtAction(
          ELiteral({
            name: ALIGNMENT_NAME,
            value,
          }),
          ALIGNMENT_PATH
        )
      );
    },
    [dispatch]
  );

  const selectedElement = useDesignerSelector(selectSelectedElement);

  if (path == null || selectedElement == null || pathValue == null) return null;

  return (
    <RiveProvider
      key={`${selectedElement.id}-${pathValue.src}${artboardValue}${stateMachineValue}${autoPlayValue}${isTouchScrollEnabledValue}`}
      artboardValue={artboardValue}
      stateMachineValue={stateMachineValue}
      onArtboardChange={onArtboardChange}
      onStateMachineChange={onStateMachineChange}
      onAutoPlayChange={onAutoPlayChange}
      onIsTouchScrollEnabledChange={onIsTouchScrollEnabledChange}
      onFitChange={onFitChange}
      onAlignmentChange={onAlignmentChange}
      onAutomaticallyHandleEventsChange={onAutomaticallyHandleEventsChange}
    >
      <VStack>
        <ExpressionEditor dispatch={eeDispatch('path')} expression={path} />
        {hasSrc ? (
          <VStack>
            <Text font="heading">Animation</Text>
            <ExpressionEditor
              dispatch={eeDispatch('artboard')}
              expression={artboard || artboardDefault()}
            />
            <ExpressionEditor
              dispatch={eeDispatch('stateMachine')}
              expression={stateMachine || stateMachineDefault()}
            />
            <Divider role="separator" />
            <Text font="heading">Layout</Text>
            <ExpressionEditor
              dispatch={eeDispatch('fit')}
              expression={fit || fitDefault()}
            />
            <ExpressionEditor
              dispatch={eeDispatch('alignment')}
              expression={alignment || alignmentDefault()}
            />
            <Divider role="separator" />
            <ExpressionEditor
              dispatch={eeDispatch('autoPlay')}
              expression={autoPlay || autoPlayDefault()}
            />
            <ExpressionEditor
              dispatch={eeDispatch('isTouchScrollEnabled')}
              expression={isTouchScrollEnabled || isTouchScrollEnabledDefault()}
            />
            <ExpressionEditor
              dispatch={eeDispatch('automaticallyHandleEvents')}
              expression={
                automaticallyHandleEvents || automaticallyHandleEventsDefault()
              }
            />
          </VStack>
        ) : null}
      </VStack>
    </RiveProvider>
  );
};

export const riveType = TypeImplementation.Alias({
  type: riveAliasType,
  render: RiveRender,
});

export const RIVE_PLACEHOLDER = {
  assetName: 'wf-rive-placeholder.riv',
  getPlaceholderData: (url: string) => ({
    id: 'plugins/Animation/assets/wf-rive-placeholder.riv',
    src: url,
    origFileName: 'wf-rive-placeholder',
  }),
} as const;
